<template>
  <MainLayout class="video-course-details">
    <div class="container mw-960" slot="action-bar">
      <div class="row">
        <div class="col-md-6 col-lg-7">
          <h5 class="action-header video-course-details__header-title typography--headline typography--color-secondary pt-1 pl-10-mobile pr-10-mobile"
              :title="`${$t('video_lectures.sub_header.video_courses')} > ${courseTitle}`">
            {{ $t('video_lectures.sub_header.video_courses') }} > {{ courseTitle }}
          </h5>

          <div class="typography--body-2 typography--color-secondary">
            <span v-if="courseDomainName">
              {{ $t('series_cards.chapters.meta.domain') }}:
              {{ courseDomainName }}
            </span>

            <span class="ml-6 mr-6" v-if="courseDomainName">
              |
            </span>

            <span>
              {{ $t('video_lectures.video_course.lecture_count', { count: lectureCount }) }}
            </span>
          </div>
        </div>

        <div class="col-md-6 col-lg-5 hidden-sm-down">
          <div class="action-btn-row">
            <router-link class="btn btn-primary float-right ml-1 mr-1" :to="`${$route.path}/upload-video-lecture`">
              {{ $t('video_lectures.video_course.create_new_lecture') }}
            </router-link>
            <router-link class="btn btn-primary float-right ml-1 mr-1" :to="`${$route.path}/edit`">
              {{ $t('video_lectures.video_course.edit') }}
            </router-link>
            <button class="btn btn-outline-primary float-right mr-1" @click="$root.$emit('show::modal', 'remove-video-course-modal')">
              {{ $t('publishers.headers.actions.remove') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="container mw-620 mt-4 pt-1" slot="layout-body" v-if="loading">
      <div class="row">
        <div class="col-12 text-center">
          <LoadingBounce></LoadingBounce>
          <div class="typography--body-2 typography--color-disabled pb-5">
            {{ $t('common.loading') }}
          </div>
        </div>
      </div>
    </div>

    <div class="container mw-620 mt-4 pt-1" slot="layout-body" v-else>
      <div>
        <div class="typography--body-1 break-word-all" v-html="courseDescription" v-if="courseDescription"/>

        <div class="typography--body-1 typography--color-disabled" v-else>
          {{ $t('common.terms.no_description') }}
        </div>
      </div>

      <hr>

      <draggable class="row"
                 v-model="lectures"
                 :options="{ handle: '.course-lecture-item__drag-handle' }"
                 @start="drag=true"
                 @end="drag=false">

        <div class="col-12 text-center mb-5 pb-5" v-if="lectures.length === 0">
          <div class="empty-list-text">
            <div class="typography--body-2 typography--color-disabled"
                 v-html="$t('video_lectures.no_lectures')">
            </div>
          </div>
        </div>

        <div class="col-12 video-course-details__lecture-row"
             v-for="(lecture, index) in lectures"
             :key="lecture.id"
             v-else>
          <CourseLectureItem
            :position="index + 1"
            :lecture="lecture"
            :domain-concepts="domainConcepts"
            :domain-name="courseDomainName"
          />
        </div>

        <InfiniteLoading :on-infinite="onInfinite" ref="infiniteLoading" class="w-100">
          <div slot="spinner">
            <LoadingBounce></LoadingBounce>
            <div class="typography--body-2 typography--color-disabled pb-5">
              {{ $t('common.loading') }}
            </div>
          </div>

          <div slot="no-more" class="col-12 text-center mb-5 pb-5">
          </div>
        </InfiniteLoading>
      </draggable>
    </div>

    <div slot="modal-area">
      <BasicModal id="remove-video-course-modal"
                  :title="$t('video_lectures.modals.remove_course.title')"
                  @ok="removeCourse()">
        <div v-html="$t('video_lectures.modals.remove_course.message')"></div>
      </BasicModal>
    </div>
  </MainLayout>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { isNil, isEmpty, sortBy, map, keyBy } from 'lodash';
  import InfiniteLoading from 'vue-infinite-loading';
  import draggable from 'vuedraggable';
  import labels from '@/mixins/labels';
  import publisherOnly from '@/mixins/publisherOnly';
  import CourseLectureItem from '../components/CourseLectureItem';

  export default {
    name: 'video-course-details',

    mixins: [
      publisherOnly,
      labels,
    ],

    components: {
      InfiniteLoading,
      CourseLectureItem,
      draggable,
    },

    data() {
      return {
        domainConceptList: [],
        loading: false,
      };
    },

    computed: {
      ...mapGetters([
        'lecturesByCourseId',
        'videoLectures',
        'videoCourses',
        'subjectMap',
        'gradeMap',
      ]),

      course() {
        return this.videoCourses[this.$route.params.course_id];
      },

      subjectName() {
        return this.getSubjectLabel(this.course);
      },

      gradeName() {
        return this.getGradeLabel(this.course);
      },

      courseTitle() {
        if (isNil(this.course)) {
          return '';
        }

        return `${this.gradeName} ${this.subjectName}: ${this.course.title}`;
      },

      courseDescription() {
        if (this.course && this.course.description) {
          return this.course.description.replace(/\n/g, '<br>');
        }

        return null;
      },

      courseDomainName() {
        if (isNil(this.course) || isEmpty(this.course.domain)) {
          return '';
        }

        return this.course.domain.name;
      },

      domainConcepts() {
        return keyBy(this.domainConceptList, dc => (dc.id));
      },

      lectureCount() {
        if (isNil(this.course)) {
          return 0;
        }

        return this.course.lecture_count || 0;
      },

      lectures: {
        get() {
          const lectures = this.lecturesByCourseId[this.$route.params.course_id] || [];

          return sortBy(lectures, 'position');
        },

        set(list) {
          const newList = map(list, (item, i) => ({
            ...item,
            position: i + 1,
          }));

          const modifiedItems = newList.filter(item => (
            item.position !== this.videoLectures[item.id].position
          ));

          Promise.all(map(modifiedItems, item => (
            this.updateVideoLecture({
              id: item.id,
              position: item.position,
            })
          )));
        },
      },
    },

    methods: {
      ...mapActions([
        'fetchDomainConceptList',
        'fetchMyVideoLectureList',
        'updateVideoLecture',
        'removeVideoCourse',
        'fetchVideoCourse',
      ]),

      removeCourse() {
        this.removeVideoCourse(this.course.id)
          .then(() => (
            this.$router.replace('/video-courses')
          ));
      },

      onInfinite() {
        const perPage = 10;
        const page = parseInt(this.lectures.length / perPage, 10) + 1;
        const params = {
          video_course_id: this.$route.params.course_id,
          per_page: perPage,
          page,
        };

        return this.fetchMyVideoLectureList(params)
          .then((courseLectureList) => {
            if (this.$refs.infiniteLoading) {
              this.$refs.infiniteLoading.$emit('$InfiniteLoading:loaded');

              if (courseLectureList.length < perPage) {
                this.$refs.infiniteLoading.$emit('$InfiniteLoading:complete');
              }
            }
          });
      },
    },

    beforeMount() {
      this.loading = true;

      const courseId = this.$route.params.course_id;

      Promise.resolve()
        .then(() => {
          if (isEmpty(this.videoCourses[courseId])) {
            return this.fetchVideoCourse(courseId);
          }

          return this.videoCourses[courseId];
        })
        .then(() => {
          if (this.course.domain_id) {
            const params = {
              domain_id: this.course.domain_id,
              per_page: 50,
            };
  
            return this.fetchDomainConceptList(params);
          }

          return [];
        })
        .then((domainConceptList) => {
          this.domainConceptList = domainConceptList;
          this.loading = false;
        });
    },
  };
</script>

<style lang="scss" scoped>
  .video-course-details {
    .video-course-details__header-title {
      overflow: hidden;
      text-overflow: ellipsis;
      word-wrap: break-word;
      word-break: break-all;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1; /* number of lines to show */
      max-height: 1.5em;    /* fallback */
    }
  }
</style>



// WEBPACK FOOTER //
// src/app/video-courses/views/VideoCourseDetails.vue